import {getCurrentWizardNavItems} from "../../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions";
import {CABINET_BOXES, SQFT_FEATURES} from "../../../sqft/constants";
import {getNextWizardSteps} from "../../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions2";
import store from "../../../../store";

const findMatchingWizardGroup = (wizardSteps, conflictingStep, wizardGroup) => {
  let nextStep = wizardSteps.find(step => step.stepIndex === conflictingStep.stepIndex);

  if (!nextStep || nextStep.stepGroups?.[0]?.groupName !== wizardGroup.groupName) {
    nextStep = wizardSteps.find(step =>
      step.stepGroups?.some(stepGroup => stepGroup.groupName === wizardGroup.groupName)
    );
  }

  const matchedGroup = nextStep?.stepGroups?.find(
    stepGroup => stepGroup.groupName === wizardGroup.groupName
  );

  return { nextStep, matchedGroup };
};

const hasFeaturesInWizardGroup = (wizardSelectedValues, stepIndex, wizardActiveItem, wizardGroup) => {
  if (!wizardGroup || stepIndex === undefined) {
    return false;
  }

  return getCurrentWizardNavItems(wizardSelectedValues, stepIndex, wizardActiveItem, wizardGroup).length > 0;
};

/**
 * Categorize conflicting cart items into items to remove and items to update
 * @returns {Object} Object with itemsToRemove and itemsToUpdate arrays
 */
export const categorizeConflictingCartItemsFromStore = () => {
  const state = store.getState();
  const { wizardConflictingSteps, wizardList, wizardActiveStep, wizardSelectedValues, wizardActiveItem } = state.wizardState;
  const { cartItems } = state.cartState;
  const { sqftCartItems } = state.sqftResourceState;
  const cartItemsAndSQFTTempCartItems = [...sqftCartItems, ...cartItems];

  const { itemsToRemove, itemsToUpdate } = categorizeConflictingCartItems(
    wizardConflictingSteps,
    cartItemsAndSQFTTempCartItems,
    wizardList,
    wizardActiveStep,
    wizardSelectedValues,
    wizardActiveItem,
  );

  return {
    itemsToRemove,
    itemsToUpdate,
    itemsToUpdateGrouped: groupItemsToUpdate(itemsToUpdate),
  };
};

export const categorizeConflictingCartItems = (
  wizardConflictingSteps,
  cartItemsAndSQFTTempCartItems,
  wizardList,
  wizardActiveStep,
  wizardSelectedValues,
  wizardActiveItem,
) => {
  void wizardList;
  const itemsToRemove = [];
  const itemsToUpdate = [];

  const sortedWizardConflictingSteps = wizardConflictingSteps?.sort((a, b) => a.stepIndex - b.stepIndex);
  const wizardSteps = getNextWizardSteps(wizardActiveItem, wizardActiveStep, wizardSelectedValues);

  for (const wizardConflictingStep of sortedWizardConflictingSteps) {
    const cartItem = cartItemsAndSQFTTempCartItems.find(
      item => wizardConflictingStep.cartItemId === item.id
    );

    if (!cartItem) {
      continue;
    }

    const partId = cartItem.partId;
    const isSlab = partId === SQFT_FEATURES.slabs;
    const isCabinetBox = CABINET_BOXES.includes(partId);
    const isInstallation = partId === "installation";

    // Skip conflict for slab, cabinet boxes, and installation
    if (isSlab || isCabinetBox || isInstallation) {
      continue;
    }

    const wizardGroup = wizardActiveItem.wizardGroups.find(
      wizGroup => wizGroup.id === wizardConflictingStep.wizardGroupId
    );

    if (!wizardGroup) {
      console.error('wizardGroup was not found for the wizardConflictingStep');
      continue;
    }

    const { nextStep, matchedGroup } = findMatchingWizardGroup(wizardSteps, wizardConflictingStep, wizardGroup);
    const hasFeatures = hasFeaturesInWizardGroup(wizardSelectedValues, nextStep?.stepIndex, wizardActiveItem, matchedGroup);
    const shouldRemoveCartItem = !matchedGroup || !hasFeatures;

    if (shouldRemoveCartItem) {
      itemsToRemove.push({cartItem, wizardConflictingStep});
      continue;
    }

    itemsToUpdate.push({cartItem, wizardConflictingStep});
  }

  return {
    itemsToRemove,
    itemsToUpdate
  };
};

/**
 * Group cart items with same feature and partId into one step.
 */
export const groupItemsToUpdate = (itemsToUpdate) => {
  const map = new Map();

  for (const itemData of itemsToUpdate) {
    const { cartItem, wizardConflictingStep } = itemData;

    const partId = cartItem.partId;
    const incompatibleFeatureId = cartItem?.cartFeature?.dbFeature?.id;

    const key = [
      partId ?? 'noPart',
      incompatibleFeatureId ?? 'noFeature',
      wizardConflictingStep?.wizardGroupGroupName ?? 'noWizardGroupGroupName',
      wizardConflictingStep?.stepIndex ?? 'noStepIndex',
      wizardConflictingStep?.wizardGroupId ?? 'noGroup',
    ].join(':');

    if (!map.has(key)) {
      map.set(key, {
        key,
        partId,
        incompatibleFeatureId,
        wizardConflictingStep,
        cartItemIds: [],
        representative: itemData,
        items: [],
      });
    }

    const group = map.get(key);
    group.cartItemIds.push(cartItem.id);
    group.items.push(itemData);
  }

  return Array.from(map.values());
};
